import type { CalcPedido, CalcProduto } from '../api/crm';

function extrairPotenciaDosModulos(nomeModulo: string, qtd: number): number {
  if (!nomeModulo || !qtd) return 0;
  const match = nomeModulo.match(/(\d+(?:\.\d+)?)\s*[Ww](?!\w)/);
  if (match) return (parseFloat(match[1]) * qtd) / 1000;
  return 0;
}

// Potência (kW) de UMA unidade do inversor, extraída da descrição
// (ex.: "INVERSOR 6KW BI 220V - HUAWEI SUN2000-6KTL-L1" -> 6).
export function extrairPotenciaUnitariaInversorKW(desc: string): number {
  if (!desc) return 0;
  const matchKW = desc.match(/(\d+(?:[.,]\d+)?)\s*[Kk][Ww]/);
  if (matchKW) return parseFloat(matchKW[1].replace(',', '.'));
  const matchK = desc.match(/(\d+(?:[.,]\d+)?)\s*K(?=[^a-zA-Z]|[Ww]|TL|$)/i);
  if (matchK) return parseFloat(matchK[1].replace(',', '.'));
  const matchKAny = desc.match(/(\d+(?:[.,]\d+)?)\s*K/i);
  if (matchKAny) return parseFloat(matchKAny[1].replace(',', '.'));
  const matchW = desc.match(/(\d+(?:[.,]\d+)?)\s*[Ww](?!\w)/);
  if (matchW) return parseFloat(matchW[1].replace(',', '.')) / 1000;
  return 0;
}

export interface InversorProposta {
  modelo: string;
  quantidade: number;
}

// "1, 1, 1" → 3. O calc grava QuantidadeInversor como CSV quando a proposta mistura modelos.
function somarQuantidadesCsv(v?: string | number | null): number {
  if (v == null) return 0;
  return String(v).split(',').reduce((s, x) => s + (parseInt(x.trim(), 10) || 0), 0);
}

// "SOLPLANET, SOLPLANET, SOLIS" → "SOLPLANET, SOLIS"
function dedupMarcasCsv(v?: string | null): string {
  if (!v) return '';
  return Array.from(new Set(String(v).split(',').map((x) => x.trim()).filter(Boolean))).join(', ');
}

// Agrupa os itens de inversor da proposta por descrição, somando as quantidades.
export function agruparInversores(itens: CalcProduto[]): InversorProposta[] {
  const out: InversorProposta[] = [];
  for (const it of itens) {
    const modelo = (it.Descricao || '').trim();
    if (!modelo) continue;
    const qtd = parseInt(it.Quantidade || '0', 10) || 0;
    const existente = out.find((x) => x.modelo === modelo);
    if (existente) existente.quantidade += qtd;
    else out.push({ modelo, quantidade: qtd });
  }
  return out;
}

export interface DadosProposta {
  idProposta: string | null;
  numeroProposta: string;
  valorProposta: number;
  qtdModulos: number;
  modeloModulo: string;
  marcaInversores: string;
  qtdInversores: number;
  qtdInversor1: number;
  modeloInversor1: string;
  qtdInversor2: number;
  modeloInversor2: string;
  qtdInversor3: number;
  modeloInversor3: string;
  /** Lista completa (modelos iguais agrupados); os campos 1..3 seguem o padrão do contrato. */
  inversores: InversorProposta[];
  potenciaProjeto: number;
  potenciaTotalInversores: number;
  tipoEstruturaInstalacao: string;
}

// Mapeia o TipoTelhado da proposta (calc_pedidos) para a opção do campo
// "Tipo de Estrutura" da homologação (tipo_estrutura_instalacao).
// COLONIAL não tem opção própria → mapeado para Fibrocimento (decisão do negócio).
const TELHADO_TO_ESTRUTURA: Record<string, string> = {
  METALICO: 'Metalico',
  COLONIAL: 'Fibrocimento',
  LAJE: 'Laje',
  SOLO: 'Solo',
};

export function mapTelhadoParaEstrutura(tipoTelhado?: string): string {
  return TELHADO_TO_ESTRUTURA[String(tipoTelhado || '').trim().toUpperCase()] || '';
}

export function extrairDadosPedido(pedido: CalcPedido): DadosProposta {
  let produtos: CalcProduto[] = [];
  try {
    produtos = typeof pedido.Produtos === 'string' ? JSON.parse(pedido.Produtos) : pedido.Produtos || [];
  } catch {
    produtos = [];
  }

  const modulos = produtos.filter((p) => {
    const desc = (p.Descricao || '').toUpperCase();
    return p.Tipo === 'MODULO' || desc.includes('MODULO') || desc.includes('MÓDULO');
  });

  let inversores = produtos.filter((p) => {
    const desc = (p.Descricao || '').toUpperCase();
    if (desc.includes('SUPORTE') || desc.includes('ESTRUTURA')) return false;
    return p.Tipo === 'INVERSOR' || desc.includes('INVERSOR');
  });

  if (inversores.length === 0 && pedido.KitFechado === 'sim') {
    const kitItems = produtos.filter((p) => (p.ContaEstoque || '').toUpperCase() === 'KIT');
    inversores = kitItems.filter((p) => {
      const desc = (p.Descricao || '').toUpperCase();
      if (desc.includes('MODULO') || desc.includes('MÓDULO')) return false;
      if (desc.includes('SUPORTE') || desc.includes('ESTRUTURA')) return false;
      if (/^\d+[Ww]?$/.test((p.Descricao || '').trim())) return false;
      return true;
    });
  }

  const qtdModulos = parseInt(pedido.QuantidadeModulo || '0') ||
    modulos.reduce((sum, m) => sum + (parseInt(m.Quantidade || '0') || 0), 0);

  const qtdInversoresFromProducts = inversores.reduce((sum, i) => sum + (parseInt(i.Quantidade || '0') || 0), 0);
  const qtdInversores = (pedido.KitFechado === 'sim' && qtdInversoresFromProducts > 0)
    ? qtdInversoresFromProducts
    : (somarQuantidadesCsv(pedido.QuantidadeInversor) || qtdInversoresFromProducts);

  let modeloModulo = modulos.length > 0 ? modulos[0].Descricao || '' : '';
  if (!modeloModulo && pedido.PotenciaModulo) modeloModulo = `MODULO ${pedido.PotenciaModulo}W`;
  // Anexa a MarcaModulo pra deixar o modelo completo (ex: "MODULO 610W - DAH")
  const marcaModulo = (pedido as { MarcaModulo?: string }).MarcaModulo;
  if (modeloModulo && marcaModulo && !modeloModulo.toUpperCase().includes(String(marcaModulo).toUpperCase())) {
    modeloModulo = `${modeloModulo} - ${marcaModulo}`;
  }

  const listaInversores = agruparInversores(inversores);
  const modeloInversor = listaInversores[0]?.modelo || '';
  const marcaInversor = dedupMarcasCsv(pedido.MarcaInversor) || (modeloInversor ? modeloInversor.split(' ')[0] : '');
  // Um modelo só: a quantidade dele é o total (comportamento de sempre, cobre o caso em
  // que QuantidadeInversor do pedido difere da soma dos itens).
  const qtdPorModelo = (i: number) => (listaInversores.length === 1 && i === 0)
    ? qtdInversores
    : (listaInversores[i]?.quantidade || 0);

  const potenciaProjeto = extrairPotenciaDosModulos(modeloModulo, qtdModulos)
    || (pedido.PotenciaProjeto ? Number(pedido.PotenciaProjeto) / 1000 : 0);

  const potenciaTotalInversoresFromDesc = inversores.reduce((total, inv) => {
    const qty = parseInt(inv.Quantidade || '0') || 0;
    return total + extrairPotenciaUnitariaInversorKW(inv.Descricao || '') * qty;
  }, 0);

  const potenciaTotalInversores = potenciaTotalInversoresFromDesc > 0
    ? potenciaTotalInversoresFromDesc
    : (() => {
        const potW = parseFloat(pedido.PotenciaInversor || '0') || 0;
        const qty = somarQuantidadesCsv(pedido.QuantidadeInversor) || 1;
        return potW > 0 ? (potW / 1000) * qty : 0;
      })();

  const valorProposta = Number(pedido.ValorTotalDescontado) > 0
    ? Number(pedido.ValorTotalDescontado)
    : Number(pedido.ValorTotalPedido || 0);

  return {
    idProposta: pedido.IdPedidoBanco || null,
    numeroProposta: pedido.NumeroProposta || '',
    valorProposta,
    qtdModulos,
    modeloModulo,
    marcaInversores: marcaInversor,
    qtdInversores,
    qtdInversor1: qtdPorModelo(0),
    modeloInversor1: modeloInversor,
    qtdInversor2: qtdPorModelo(1),
    modeloInversor2: listaInversores[1]?.modelo || '',
    qtdInversor3: qtdPorModelo(2),
    modeloInversor3: listaInversores[2]?.modelo || '',
    inversores: listaInversores.length === 1
      ? [{ modelo: modeloInversor, quantidade: qtdInversores }]
      : listaInversores,
    potenciaProjeto: Math.floor(potenciaProjeto * 100) / 100,
    potenciaTotalInversores: Math.round(potenciaTotalInversores * 100) / 100,
    tipoEstruturaInstalacao: mapTelhadoParaEstrutura(pedido.TipoTelhado),
  };
}

// Filtra pedidos válidos (com produtos e dentro da data de validade)
export function filtrarPropostasValidas(pedidos: CalcPedido[]): CalcPedido[] {
  const agora = new Date();
  return pedidos
    .filter((p) => {
      let prods: CalcProduto[] = [];
      try { prods = typeof p.Produtos === 'string' ? JSON.parse(p.Produtos) : p.Produtos || []; } catch { /* */ }
      if (!prods.length) return false;
      const validade = p.DataValidadeProposta
        ? new Date(p.DataValidadeProposta)
        : p.Data
          ? new Date(new Date(p.Data).getTime() + 2 * 24 * 60 * 60 * 1000)
          : new Date(0);
      return validade >= agora;
    })
    .sort((a, b) => new Date(b.Data || 0).getTime() - new Date(a.Data || 0).getTime());
}
